import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getOpportunity, deleteOpportunity, shareOpportunity, unshareOpportunity, formatApiError } from "../lib/api";
import { OpportunityIntelligence } from "../components/OpportunityIntelligence";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

function ShareBox({ id, initialToken, initialEnabled }) {
  const [link, setLink] = useState(
    initialEnabled && initialToken ? `${window.location.origin}/r/${initialToken}` : null
  );
  const [busy, setBusy] = useState(false);

  const enable = async () => {
    setBusy(true);
    try {
      const d = await shareOpportunity(id);
      const url = `${window.location.origin}${d.sharePath}`;
      setLink(url);
      try { await navigator.clipboard.writeText(url); toast.success("Share link copied to clipboard."); }
      catch { toast.success("Share link ready."); }
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not create link."); }
    finally { setBusy(false); }
  };

  const revoke = async () => {
    setBusy(true);
    try { await unshareOpportunity(id); setLink(null); toast.success("Share link revoked."); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!link) {
    return (
      <Button variant="outline" className="h-9" onClick={enable} disabled={busy} data-testid="report-share">
        {busy ? "Creating…" : "Share with dealer"}
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-2 rounded-md border border-atlas-blue/30 bg-atlas-blue/5 pl-3 pr-1 py-1" data-testid="report-share-box">
      <input readOnly value={link} onClick={(e) => e.target.select()} data-testid="report-share-link"
        className="w-[220px] md:w-[300px] bg-transparent text-xs text-slate-600 font-mono focus:outline-none" />
      <Button variant="outline" className="h-7 px-2 text-xs" onClick={() => { navigator.clipboard.writeText(link); toast.success("Copied."); }} data-testid="report-share-copy">Copy</Button>
      <Button variant="outline" className="h-7 px-2 text-xs text-rose-600 border-rose-200" onClick={revoke} disabled={busy} data-testid="report-share-revoke">Revoke</Button>
    </div>
  );
}

export default function OpportunityReportPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [opp, setOpp] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    getOpportunity(id).then((d) => setOpp(d.opportunity)).catch((e) => setErr(formatApiError(e?.response?.data?.detail) || "Not found"));
  }, [id]);

  const remove = async () => {
    if (!window.confirm("Delete this opportunity?")) return;
    try { await deleteOpportunity(id); toast.success("Deleted."); navigate("/intelligence"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <>
      <div className="max-w-[1100px] mx-auto px-5 md:px-8 py-8">
        <div className="flex items-center justify-between mb-5 no-print">
          <Link to="/intelligence" className="text-sm text-atlas-blue hover:underline" data-testid="report-back">← All opportunities</Link>
          {user?.role === "admin" && opp && (
            <div className="flex flex-wrap items-center gap-2">
              <ShareBox id={id} initialToken={opp.shareToken} initialEnabled={opp.shareEnabled} />
              <Button variant="outline" className="h-9" onClick={() => window.print()} data-testid="report-pdf">Download PDF</Button>
              <Button variant="outline" className="h-9" onClick={() => navigate("/intelligence/new")} data-testid="report-new">New analysis</Button>
              <Button variant="outline" className="h-9 text-rose-600 border-rose-200" onClick={remove} data-testid="report-delete">Delete</Button>
            </div>
          )}
        </div>
        {err ? <div className="py-20 text-center text-slate-400" data-testid="report-error">{err}</div>
          : !opp ? <div className="py-20 text-center text-slate-400">Loading report…</div>
          : (
            <div data-testid="opportunity-report" className="print-area">
              <div className="mb-5">
                <span className="text-[11px] uppercase tracking-widest text-slate-400 font-mono">Atlas Opportunity Report</span>
                <h1 className="text-2xl font-bold text-atlas-ink mt-1">Should you buy this vehicle?</h1>
                {opp.notes && <p className="text-slate-500 text-sm mt-1">{opp.notes}</p>}
              </div>
              <OpportunityIntelligence data={opp} />
              <p className="text-[11px] text-slate-400 mt-6">Atlas provides acquisition intelligence, not the transaction. Figures are calculated from operator-entered data and real ECB FX rates; verify assumptions before purchase. Created {opp.createdAt ? new Date(opp.createdAt).toLocaleString() : ""}.</p>
            </div>
          )}
      </div>
    </>
  );
}
